import { WEBEX_CALLING_VERSION } from "./webexCallingVersion";

/** What the portal keeps of a Webex access token. The SDK reads these three fields. */
export type SdkToken = { access_token: string; expires_in: number; refresh_token: string };

export type CallingInitOptions = {
  webexConfig: {
    config: {
      logger: { level: "error" };
      // webex-core reads refreshCallback from its config (Token.config is config.credentials), not
      // from the credentials it is given; a callback among those is never called.
      credentials: { refreshCallback: (webex: unknown, token: unknown) => Promise<SdkToken> };
    };
    credentials: {
      access_token: string;
      expires_in: number;
      // The SDK refreshes credentials only while a refresh_token is present, so the portal
      // passes a placeholder here; the backend keeps the real refresh token and renews the token.
      refresh_token: string;
    };
  };
  callingConfig: {
    clientConfig: {
      calling: boolean;
      contact: boolean;
      callHistory: boolean;
      callSettings: boolean;
      voicemail: boolean;
    };
    callingClientConfig: { logger: { level: "error" } };
    logger: { level: "error" };
  };
};

/** The SDK's microphone stream (@webex/media-helpers LocalMicrophoneStream), as far as the portal uses it. */
export type LocalMicrophoneStream = {
  outputStream: MediaStream;
  setUserMuted(muted: boolean): void;
  stop(): void;
};

/** What the SDK knows of the remote party; the number is absent for a withheld caller. */
export type CallerInfo = { name?: string; num?: string };

export type CallingCall = {
  // The SDK's Call implementation is async (its interface declares void); both settle once the media is set up.
  dial(stream: LocalMicrophoneStream): Promise<void>;
  answer(stream: LocalMicrophoneStream): Promise<void>;
  end(): void;
  getCorrelationId(): string;
  getCallerInfo(): CallerInfo;
  on(event: "connect" | "established" | "disconnect", callback: () => void): unknown;
  on(event: "remote_media", callback: (track: MediaStreamTrack) => void): unknown;
  on(event: "call_error", callback: (error: unknown) => void): unknown;
  on(event: "caller_id", callback: (display: { correlationId: string; callerId: CallerInfo }) => void): unknown;
};

export type CallingLine = {
  on(event: "registered" | "unregistered", callback: () => void): unknown;
  on(event: "error", callback: (error: unknown) => void): unknown;
  on(event: "line:incoming_call", callback: (call: CallingCall) => void): unknown;
  register(): Promise<void>;
  deregister(): Promise<void>;
  makeCall(destination: { type: "uri"; address: string }): CallingCall | undefined;
};

export type CallingInstance = {
  on(event: "ready", callback: () => void): unknown;
  register(): Promise<void>;
  deregister(): Promise<void>;
  callingClient?: { getLines(): Record<string, CallingLine> };
  // webex-core's derived `ready` property: true once the SDK finished loading, whether or not
  // the "ready" event was already emitted when the portal subscribed to it.
  webex?: {
    ready?: boolean;
    // webex-core's Credentials#refresh(): the SDK's own renewal, which calls the portal's refreshCallback.
    credentials?: { refresh(): Promise<unknown> };
  };
};

export type WebexCallingGlobal = {
  init(options: CallingInitOptions): Promise<CallingInstance>;
  // The SDK's optional argument is a set of audio device constraints; the portal uses the defaults.
  createMicrophoneStream(): Promise<LocalMicrophoneStream>;
};

declare global {
  interface Window {
    Calling?: WebexCallingGlobal;
  }
}

let loading: Promise<WebexCallingGlobal> | null = null;

export const webexCallingSource = (): string =>
  `${process.env.PUBLIC_URL}/vendor/webex-calling-${WEBEX_CALLING_VERSION}.min.js`;

export const loadWebexCalling = (): Promise<WebexCallingGlobal> => {
  if (loading) {
    return loading;
  }
  const pending = new Promise<WebexCallingGlobal>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = webexCallingSource();
    script.async = true;
    script.onload = () => {
      if (window.Calling) {
        resolve(window.Calling);
      } else {
        reject(new Error("Webex Calling SDK loaded without a Calling global"));
      }
    };
    script.onerror = () => {
      reject(new Error("Webex Calling SDK failed to load"));
    };
    document.head.appendChild(script);
  });
  loading = pending;
  pending.catch(() => {
    if (loading === pending) {
      loading = null;
    }
  });
  return pending;
};
